import axios from "axios";
import toast from "react-hot-toast";

const API_BASE_URL = process.env.REACT_APP_API_URL || "http://localhost:5000";

// Helper to check and set logout flag (shared across all services)
const checkAndSetLogoutFlag = (): boolean => {
  const flag = sessionStorage.getItem('__is_logging_out__');
  if (flag === 'true') {
    return true; // Already logging out
  }
  sessionStorage.setItem('__is_logging_out__', 'true');
  return false; // First time, proceed with logout
};

// Configure axios defaults
axios.defaults.withCredentials = true;

export interface FulfilConfig {
  mode: "live" | "test";
  live: {
    subdomain: string;
    apiKey: string;
  };
  test: {
    subdomain: string;
    apiKey: string;
  };
}

export interface ShipHeroConfig {
  mode: "live" | "test";
  live: {
    refreshToken: string;
    oauthUrl: string;
    apiBaseUrl: string;
    defaultWarehouseId?: string;
    customerAccountId?: string;
  };
  test: {
    refreshToken: string;
    oauthUrl: string;
    apiBaseUrl: string;
    defaultWarehouseId?: string;
    customerAccountId?: string;
  };
}


export interface EmailConfig {
  smtpHost: string;
  smtpPort: number;
  smtpUsername: string;
  smtpPassword: string;
  smtpUseTls: boolean;
  smtpUseSsl: boolean;
  fromEmail: string;
  fromName: string;
  isActive: boolean;
}

export interface ProductSyncConfig {
  mode: "live" | "test";
}

export interface InventorySyncConfig {
  mode: "live" | "test";
}

export interface SupplierShipmentSyncConfig {
  mode: "live" | "test";
}

export interface ConfigResponse {
  fulfil: FulfilConfig;
  shiphero: ShipHeroConfig;
}

export interface User {
  id: string;
  name?: string;
  username: string;
  email: string;
  is_admin: boolean;
  is_active: boolean;
  role?: "superadmin" | "admin" | "staff";
  created_at?: string;
  updated_at?: string;
}

export interface ServiceCodeMappingItem {
  id?: number;
  serviceCode: string;
  carrier: string;
  shippingMethod: string;
  wholesaleShippingOption?: string; // COURIER or FREIGHT
}

/** Channel xref row for Customer Shipments (channel.name match). */
export interface CustomerShipmentChannelMappingRow {
  id?: number;
  channel: string;
  tags: string[] | string;
  shipping_name?: string;
  store_profile?: string;
  account_number?: string;
  account_zip?: string;
  bill?: string;
  additional_packing_notes?: string;
  default_carrier?: string;
  default_ship_method?: string;
}

/** Channel xref row for EDI Shipments; includes trading_partner_id / trading_partner_name. */
export interface EdiShipmentChannelMappingRow extends CustomerShipmentChannelMappingRow {
  trading_partner_id?: string;
  trading_partner_name?: string;
  gs1_label_required?: boolean;
  gs1_company_prefix?: string;
  default_carrier?: string;
  default_ship_method?: string;
}

/** Per Ship-To address shipping account xref for Customer Shipments (customer.name + delivery_address.code). */
export interface CustomerShipmentAddressAccountMappingRow {
  id?: number;
  customer: string;
  address_code: string;
  account_number?: string;
  account_zip?: string;
  bill?: string;
}

class ConfigService {
  private api = axios.create({
    baseURL: API_BASE_URL,
    headers: {
      "Content-Type": "application/json",
    },
  });

  constructor() {
    // Add a request interceptor to ensure the latest token is always used
    this.api.interceptors.request.use(
      (config) => {
        const token = localStorage.getItem("authToken");
        if (token) {
          config.headers.Authorization = `Bearer ${token}`;
        }
        return config;
      },
      (error) => {
        return Promise.reject(error);
      }
    );

    // Add a response interceptor to handle 401 Unauthorized errors
    this.api.interceptors.response.use(
      (response) => {
        return response;
      },
      (error) => {
        if (error.response && error.response.status === 401) {
          const url = (error.config?.url || "").toLowerCase();
          // Do NOT redirect on 401 for the login request itself
          const isLoginCall = url.includes("/api/login");
          if (!isLoginCall && !checkAndSetLogoutFlag()) {
            // Check if we're already on the login page
            const isOnLoginPage = window.location.pathname === '/login';
            if (isOnLoginPage) {
              // Already on login page, just clear the flag and return
              sessionStorage.removeItem('__is_logging_out__');
              return Promise.reject(error);
            }
            
            const message = error.response?.data?.message || "Session expired. Please login again.";
            const reason = error.response?.data?.reason;
            
            // Show appropriate toast message (only once, using unique ID to prevent duplicates)
            if (reason === 'token_invalidated') {
              toast.error(message, { duration: 4000, id: 'token-invalidated-global' });
              // Longer delay for token invalidation so user can read the message
              localStorage.removeItem("authToken");
              setTimeout(() => {
                sessionStorage.removeItem('__is_logging_out__'); // Clear flag before redirect
                window.location.href = "/login";
              }, 3500);
            } else {
              toast.error(message, { duration: 3000, id: 'session-expired-global' });
              localStorage.removeItem("authToken");
              setTimeout(() => {
                sessionStorage.removeItem('__is_logging_out__'); // Clear flag before redirect
                window.location.href = "/login";
              }, 2500);
            }
          }
        }
        return Promise.reject(error);
      }
    );
  }

  setAuthToken(token: string) {
    this.api.defaults.headers.common["Authorization"] = `Bearer ${token}`;
  }

  async getConfig(): Promise<ConfigResponse> {
    try {
      const response = await this.api.get("/api/config");
      return response.data;
    } catch (error) {
      if (axios.isAxiosError(error)) {
        throw new Error(
          error.response?.data?.message || "Failed to fetch configuration"
        );
      }
      throw new Error("Failed to fetch configuration");
    }
  }

  async updateFulfilConfig(config: FulfilConfig): Promise<void> {
    try {
      await this.api.put("/api/config/fulfil", {
        mode: config.mode,
        live: {
          subdomain: config.live.subdomain,
          apiKey: config.live.apiKey,
        },
        test: {
          subdomain: config.test.subdomain,
          apiKey: config.test.apiKey,
        },
      });
    } catch (error) {
      if (axios.isAxiosError(error)) {
        throw new Error(
          error.response?.data?.message ||
            "Failed to update Fulfil configuration"
        );
      }
      throw new Error("Failed to update Fulfil configuration");
    }
  }

  async updateShipHeroConfig(config: ShipHeroConfig): Promise<void> {
    try {
      await this.api.put("/api/config/shiphero", {
        mode: config.mode,
        live: {
          refreshToken: config.live.refreshToken,
          oauthUrl: config.live.oauthUrl,
          apiBaseUrl: config.live.apiBaseUrl,
          defaultWarehouseId: config.live.defaultWarehouseId || "",
          customerAccountId: config.live.customerAccountId || "",
        },
        test: {
          refreshToken: config.test.refreshToken,
          oauthUrl: config.test.oauthUrl,
          apiBaseUrl: config.test.apiBaseUrl,
          defaultWarehouseId: config.test.defaultWarehouseId || "",
          customerAccountId: config.test.customerAccountId || "",
        },
      });
    } catch (error) {
      if (axios.isAxiosError(error)) {
        throw new Error(
          error.response?.data?.message ||
            "Failed to update ShipHero configuration"
        );
      }
      throw new Error("Failed to update ShipHero configuration");
    }
  }
  // SystemConfig removed

  async testFulfilConnection(): Promise<{ success: boolean; message: string }> {
    return { success: false, message: "Not supported" };
  }

  async testShipHeroConnection(): Promise<{
    success: boolean;
    message: string;
  }> {
    return { success: false, message: "Not supported" };
  }

  // Email Configuration Methods
  async getEmailConfig(): Promise<EmailConfig> {
    try {
      const response = await this.api.get("/api/config/email");
      return response.data;
    } catch (error) {
      if (axios.isAxiosError(error)) {
        throw new Error(
          error.response?.data?.message || "Failed to fetch email configuration"
        );
      }
      throw new Error("Failed to fetch email configuration");
    }
  }

  async updateEmailConfig(config: EmailConfig): Promise<void> {
    try {
      await this.api.put("/api/config/email", {
        smtpHost: config.smtpHost,
        smtpPort: config.smtpPort,
        smtpUsername: config.smtpUsername,
        smtpPassword: config.smtpPassword,
        smtpUseTls: config.smtpUseTls,
        smtpUseSsl: config.smtpUseSsl,
        fromEmail: config.fromEmail,
        fromName: config.fromName,
      });
    } catch (error) {
      if (axios.isAxiosError(error)) {
        throw new Error(
          error.response?.data?.message ||
            "Failed to update email configuration"
        );
      }
      throw new Error("Failed to update email configuration");
    }
  }

  async testEmailConfig(): Promise<{ success: boolean; message: string }> {
    try {
      const response = await this.api.post("/api/config/email/test");
      return response.data;
    } catch (error) {
      if (axios.isAxiosError(error)) {
        return {
          success: false,
          message: error.response?.data?.message || "Email test failed",
        };
      }
      return {
        success: false,
        message: "Email test failed",
      };
    }
  }

  // User management methods
  async getUsers(page = 1, perPage = 20, q?: string): Promise<{
    items: User[];
    page: number;
    per_page: number;
    total: number;
    total_pages: number;
  }> {
    try {
      const params = new URLSearchParams();
      params.set("page", String(page));
      params.set("per_page", String(perPage));
      if (q && q.trim()) params.set("q", q.trim());
      const response = await this.api.get(`/api/users?${params.toString()}`);
      const data = response.data;
      if (Array.isArray(data)) {
        return {
          items: data,
          page: 1,
          per_page: data.length,
          total: data.length,
          total_pages: 1,
        };
      }
      return data;
    } catch (error) {
      if (axios.isAxiosError(error)) {
        throw new Error(
          error.response?.data?.message || "Failed to fetch users"
        );
      }
      throw new Error("Failed to fetch users");
    }
  }

  async createUser(userData: {
    username: string;
    email: string;
    password: string;
    role: "admin" | "staff";
    name?: string;
  }): Promise<{ message: string; user: User }> {
    try {
      const response = await this.api.post("/api/users", userData);
      return response.data;
    } catch (error) {
      if (axios.isAxiosError(error)) {
        throw new Error(
          error.response?.data?.message || "Failed to create user"
        );
      }
      throw new Error("Failed to create user");
    }
  }
  
  async updateUser(userId: string, updates: { name?: string; role?: "admin" | "staff"; isActive?: boolean }): Promise<{ message: string; user: User }> {
    try {
      const response = await this.api.put(`/api/users/${userId}`, updates);
      return response.data;
    } catch (error) {
      if (axios.isAxiosError(error)) {
        throw new Error(error.response?.data?.message || "Failed to update user");
      }
      throw new Error("Failed to update user");
    }
  }
  
  async resetUserPassword(userId: string, payload: { newPassword: string; confirmPassword: string }): Promise<{ message: string }> {
    try {
      const response = await this.api.post(`/api/users/${userId}/reset-password`, payload);
      return response.data;
    } catch (error) {
      if (axios.isAxiosError(error)) {
        throw new Error(error.response?.data?.message || "Failed to reset password");
      }
      throw new Error("Failed to reset password");
    }
  }
  
  async deleteUser(userId: string): Promise<{ message: string }> {
    try {
      const response = await this.api.delete(`/api/users/${userId}`);
      return response.data;
    } catch (error) {
      if (axios.isAxiosError(error)) {
        throw new Error(error.response?.data?.message || "Failed to delete user");
      }
      throw new Error("Failed to delete user");
    }
  }

  // Product sync
  async getProductSyncStatus(): Promise<any> {
    try {
      const response = await this.api.get("/api/product-sync/status");
      return response.data;
    } catch (error) {
      if (axios.isAxiosError(error)) {
        throw new Error(
          error.response?.data?.message || "Failed to fetch product sync status"
        );
      }
      throw new Error("Failed to fetch product sync status");
    }
  }

  async getProductSyncLogs(
    page = 1,
    perPage = 20,
    q?: string,
    mode?: "live" | "test"
  ): Promise<{
    items: any[];
    page: number;
    per_page: number;
    total: number;
    total_pages: number;
  }> {
    try {
      const params = new URLSearchParams();
      params.set("page", String(page));
      params.set("per_page", String(perPage));
      if (q && q.trim()) params.set("q", q.trim());
      if (mode) params.set("mode", mode);
      const response = await this.api.get(
        `/api/product-sync/logs?${params.toString()}`
      );
      return response.data;
    } catch (error) {
      if (axios.isAxiosError(error)) {
        throw new Error(
          error.response?.data?.message || "Failed to fetch product sync logs"
        );
      }
      throw new Error("Failed to fetch product sync logs");
    }
  }

  // Product sync mode methods
  async getProductSyncMode(): Promise<ProductSyncConfig> {
    try {
      const response = await this.api.get("/api/config/product-sync/mode");
      return response.data;
    } catch (error) {
      if (axios.isAxiosError(error)) {
        throw new Error(
          error.response?.data?.message || "Failed to fetch product sync mode"
        );
      }
      throw new Error("Failed to fetch product sync mode");
    }
  }

  async updateProductSyncMode(mode: "live" | "test"): Promise<void> {
    try {
      await this.api.put("/api/config/product-sync/mode", { mode });
    } catch (error) {
      if (axios.isAxiosError(error)) {
        throw new Error(
          error.response?.data?.message || "Failed to update product sync mode"
        );
      }
      throw new Error("Failed to update product sync mode");
    }
  }

  async checkProductSync(code: string, mode: "live" | "test"): Promise<any> {
    try {
      const params = new URLSearchParams();
      params.set("code", code);
      params.set("mode", mode);
      const response = await this.api.get(
        `/api/product-sync/check?${params.toString()}`
      );
      return response.data;
    } catch (error) {
      if (axios.isAxiosError(error)) {
        throw new Error(
          error.response?.data?.message || "Failed to check product sync"
        );
      }
      throw new Error("Failed to check product sync");
    }
  }

  async runProductSync(): Promise<{ message: string; summary?: any; is_syncing?: boolean }> {
    try {
      const response = await this.api.post(`/api/product-sync/run`);
      return response.data;
    } catch (error) {
      if (axios.isAxiosError(error)) {
        if (error.response?.status === 401) {
          throw error;
        }
        const msg = error.response?.data?.message || "Failed to run product sync";
        throw new Error(msg);
      }
      throw new Error("Failed to run product sync");
    }
  }

  async syncProductByCode(productCode: string): Promise<{ message: string; product?: any }> {
    try {
      const response = await this.api.post(`/api/product-sync/sync-product/${productCode}`);
      return response.data;
    } catch (error) {
      if (axios.isAxiosError(error)) {
        if (error.response?.status === 401) {
          throw error;
        }
        const msg = error.response?.data?.message || "Failed to sync product";
        throw new Error(msg);
      }
      throw new Error("Failed to sync product");
    }
  }

  // Sync Schedule methods
  async getSyncSchedule(): Promise<string[]> {
    try {
      const response = await this.api.get("/api/config/product-sync/schedule");
      return Array.isArray(response.data?.schedule)
        ? response.data.schedule
        : [];
    } catch (error) {
      if (axios.isAxiosError(error)) {
        throw new Error(
          error.response?.data?.message || "Failed to fetch sync schedule"
        );
      }
      throw new Error("Failed to fetch sync schedule");
    }
  }

  async updateSyncSchedule(schedule: string[]): Promise<void> {
    try {
      await this.api.put("/api/config/product-sync/schedule", { schedule });
    } catch (error) {
      if (axios.isAxiosError(error)) {
        throw new Error(
          error.response?.data?.message || "Failed to update sync schedule"
        );
      }
      throw new Error("Failed to update sync schedule");
    }
  }

  // Inventory Sync methods
  async getInventorySyncStatus(): Promise<any> {
    try {
      const response = await this.api.get("/api/inventory-sync/status");
      return response.data;
    } catch (error) {
      if (axios.isAxiosError(error)) {
        throw new Error(
          error.response?.data?.message || "Failed to get inventory sync status"
        );
      }
      throw new Error("Failed to get inventory sync status");
    }
  }

  async getInventorySyncLogs(
    page = 1,
    perPage = 20,
    q?: string,
    mode?: "live" | "test"
  ): Promise<{
    items: any[];
    page: number;
    per_page: number;
    total: number;
    total_pages: number;
  }> {
    try {
      const params = new URLSearchParams({
        page: page.toString(),
        per_page: perPage.toString(),
      });
      if (q) params.append("q", q);
      if (mode) params.append("mode", mode);

      const response = await this.api.get(`/api/inventory-sync/logs?${params}`);
      return response.data;
    } catch (error) {
      if (axios.isAxiosError(error)) {
        throw new Error(
          error.response?.data?.message || "Failed to get inventory sync logs"
        );
      }
      throw new Error("Failed to get inventory sync logs");
    }
  }

  async getInventorySyncMode(): Promise<InventorySyncConfig> {
    try {
      const response = await this.api.get("/api/config/inventory-sync/mode");
      return response.data;
    } catch (error) {
      if (axios.isAxiosError(error)) {
        throw new Error(
          error.response?.data?.message || "Failed to fetch inventory sync mode"
        );
      }
      throw new Error("Failed to fetch inventory sync mode");
    }
  }

  async updateInventorySyncMode(mode: "live" | "test"): Promise<void> {
    try {
      await this.api.put("/api/config/inventory-sync/mode", { mode });
    } catch (error) {
      if (axios.isAxiosError(error)) {
        throw new Error(
          error.response?.data?.message || "Failed to update inventory sync mode"
        );
      }
      throw new Error("Failed to update inventory sync mode");
    }
  }

  async runInventorySync(): Promise<{ message: string; summary?: any; is_syncing?: boolean }> {
    try {
      const response = await this.api.post(`/api/inventory-sync/run`);
      return response.data;
    } catch (error) {
      if (axios.isAxiosError(error)) {
        if (error.response?.status === 401) {
          throw error;
        }
        const msg = error.response?.data?.message || "Failed to run inventory sync";
        throw new Error(msg);
      }
      throw new Error("Failed to run inventory sync");
    }
  }

  async syncInventoryByCode(inventoryCode: string): Promise<{ message: string; inventory?: any }> {
    try {
      const response = await this.api.post(`/api/inventory-sync/sync/${inventoryCode}`);
      return response.data;
    } catch (error) {
      if (axios.isAxiosError(error)) {
        throw new Error(
          error.response?.data?.message || `Failed to sync inventory ${inventoryCode}`
        );
      }
      throw new Error(`Failed to sync inventory ${inventoryCode}`);
    }
  }

  // Inventory Sync Schedule methods
  async getInventorySyncSchedule(): Promise<string[]> {
    try {
      const response = await this.api.get("/api/config/inventory-sync/schedule");
      return Array.isArray(response.data?.schedule)
        ? response.data.schedule
        : [];
    } catch (error) {
      if (axios.isAxiosError(error)) {
        throw new Error(
          error.response?.data?.message || "Failed to fetch inventory sync schedule"
        );
      }
      throw new Error("Failed to fetch inventory sync schedule");
    }
  }

  async updateInventorySyncSchedule(schedule: string[]): Promise<void> {
    try {
      await this.api.put("/api/config/inventory-sync/schedule", { schedule });
    } catch (error) {
      if (axios.isAxiosError(error)) {
        throw new Error(
          error.response?.data?.message || "Failed to update inventory sync schedule"
        );
      }
      throw new Error("Failed to update inventory sync schedule");
    }
  }

  // Supplier Shipment Sync Methods
  async getSupplierShipmentSyncStatus(): Promise<any> {
    try {
      const response = await this.api.get("/api/supplier-shipment-sync/status");
      return response.data;
    } catch (error) {
      if (axios.isAxiosError(error)) {
        throw new Error(
          error.response?.data?.message || "Failed to fetch supplier shipment sync status"
        );
      }
      throw new Error("Failed to fetch supplier shipment sync status");
    }
  }

  async getSupplierShipmentSyncLogs(
    page = 1,
    perPage = 20,
    q?: string,
    mode?: "live" | "test"
  ): Promise<{
    items: any[];
    page: number;
    per_page: number;
    total: number;
    total_pages: number;
  }> {
    try {
      const params = new URLSearchParams({
        page: page.toString(),
        per_page: perPage.toString(),
      });
      if (q) params.append("q", q);
      if (mode) params.append("mode", mode);

      const response = await this.api.get(`/api/supplier-shipment-sync/logs?${params}`);
      return response.data;
    } catch (error) {
      if (axios.isAxiosError(error)) {
        throw new Error(
          error.response?.data?.message || "Failed to fetch supplier shipment sync logs"
        );
      }
      throw new Error("Failed to fetch supplier shipment sync logs");
    }
  }

  async getSupplierShipmentSyncMode(): Promise<SupplierShipmentSyncConfig> {
    try {
      const response = await this.api.get("/api/config/supplier-shipment-sync/mode");
      return response.data;
    } catch (error) {
      if (axios.isAxiosError(error)) {
        throw new Error(
          error.response?.data?.message || "Failed to fetch supplier shipment sync mode"
        );
      }
      throw new Error("Failed to fetch supplier shipment sync mode");
    }
  }

  async updateSupplierShipmentSyncMode(mode: "live" | "test"): Promise<void> {
    try {
      await this.api.put("/api/config/supplier-shipment-sync/mode", { mode });
    } catch (error) {
      if (axios.isAxiosError(error)) {
        throw new Error(
          error.response?.data?.message || "Failed to update supplier shipment sync mode"
        );
      }
      throw new Error("Failed to update supplier shipment sync mode");
    }
  }

  async runSupplierShipmentSync(): Promise<{ message: string; summary?: any; is_syncing?: boolean }> {
    try {
      const response = await this.api.post("/api/supplier-shipment-sync/run");
      return response.data;
    } catch (error) {
      if (axios.isAxiosError(error)) {
        if (error.response?.status === 401) {
          throw error;
        }
        throw new Error(
          error.response?.data?.message || "Failed to run supplier shipment sync"
        );
      }
      throw new Error("Failed to run supplier shipment sync");
    }
  }

  async getSupplierShipmentSyncSchedule(): Promise<string[]> {
    try {
      const response = await this.api.get("/api/config/supplier-shipment-sync/schedule");
      return response.data.schedule || [];
    } catch (error) {
      if (axios.isAxiosError(error)) {
        throw new Error(
          error.response?.data?.message || "Failed to fetch supplier shipment sync schedule"
        );
      }
      throw new Error("Failed to fetch supplier shipment sync schedule");
    }
  }

  async updateSupplierShipmentSyncSchedule(schedule: string[]): Promise<void> {
    try {
      await this.api.put("/api/config/supplier-shipment-sync/schedule", { schedule });
    } catch (error) {
      if (axios.isAxiosError(error)) {
        throw new Error(
          error.response?.data?.message || "Failed to update supplier shipment sync schedule"
        );
      }
      throw new Error("Failed to update supplier shipment sync schedule");
    }
  }

  // Supplier Shipments listing and actions
  async listSupplierShipments(
    page = 1,
    perPage = 20,
    q?: string,
    mode?: "live" | "test"
  ): Promise<{
    items: any[];
    page: number;
    per_page: number;
    total: number;
    total_pages: number;
    current_mode: string;
  }> {
    try {
      const params = new URLSearchParams({
        page: page.toString(),
        per_page: perPage.toString(),
      });
      if (q) params.append("q", q);
      if (mode) params.append("mode", mode);
      const response = await this.api.get(`/api/supplier-shipments?${params}`);
      return response.data;
    } catch (error) {
      if (axios.isAxiosError(error)) {
        throw new Error(
          error.response?.data?.message || "Failed to fetch supplier shipments"
        );
      }
      throw new Error("Failed to fetch supplier shipments");
    }
  }

  async getSupplierShipmentHistory(
    shipmentNumber: string,
    mode?: "live" | "test"
  ): Promise<{ items: any[]; total: number; current_mode: string }> {
    try {
      const params = new URLSearchParams();
      if (mode) params.append("mode", mode);
      const response = await this.api.get(
        `/api/supplier-shipments/${encodeURIComponent(shipmentNumber)}/history?${params}`
      );
      return response.data;
    } catch (error) {
      if (axios.isAxiosError(error)) {
        throw new Error(
          error.response?.data?.message || "Failed to fetch shipment history"
        );
      }
      throw new Error("Failed to fetch shipment history");
    }
  }

  async syncShipHeroReceiptsForShipment(
    shipmentNumber: string
  ): Promise<{ status: string; message?: string; details?: any }> {
    try {
      const response = await this.api.post(
        `/api/supplier-shipments/${encodeURIComponent(shipmentNumber)}/sync-receipts`
      );
      return response.data;
    } catch (error) {
      if (axios.isAxiosError(error)) {
        throw new Error(
          error.response?.data?.message || "Failed to sync ShipHero receipts"
        );
      }
      throw new Error("Failed to sync ShipHero receipts");
    }
  }

  async retrySupplierShipmentSync(
    shipmentId: number
  ): Promise<{ status: string; message?: string; details?: any }> {
    try {
      const response = await this.api.post(
        `/api/supplier-shipments/${shipmentId}/retry`
      );
      return response.data;
    } catch (error: any) {
      throw new Error(
        error.response?.data?.message || "Failed to retry shipment sync"
      );
    }
  }

  // Customer Shipment Sync Methods
  async getCustomerShipmentSyncMode(): Promise<{ mode: "live" | "test" }> {
    try {
      const response = await this.api.get("/api/config/customer-shipment-sync/mode");
      return response.data;
    } catch (error) {
      if (axios.isAxiosError(error)) {
        throw new Error(
          error.response?.data?.message || "Failed to fetch customer shipment sync mode"
        );
      }
      throw new Error("Failed to fetch customer shipment sync mode");
    }
  }

  async updateCustomerShipmentSyncMode(mode: "live" | "test"): Promise<void> {
    try {
      await this.api.put("/api/config/customer-shipment-sync/mode", { mode });
    } catch (error) {
      if (axios.isAxiosError(error)) {
        throw new Error(
          error.response?.data?.message || "Failed to update customer shipment sync mode"
        );
      }
      throw new Error("Failed to update customer shipment sync mode");
    }
  }

  async getCustomerShipmentSyncSchedule(): Promise<{ schedule: string[] }> {
    try {
      const response = await this.api.get("/api/config/customer-shipment-sync/schedule");
      return response.data;
    } catch (error) {
      if (axios.isAxiosError(error)) {
        throw new Error(
          error.response?.data?.message || "Failed to fetch customer shipment sync schedule"
        );
      }
      throw new Error("Failed to fetch customer shipment sync schedule");
    }
  }

  async updateCustomerShipmentSyncSchedule(schedule: string[]): Promise<void> {
    try {
      await this.api.put("/api/config/customer-shipment-sync/schedule", { schedule });
    } catch (error) {
      if (axios.isAxiosError(error)) {
        throw new Error(
          error.response?.data?.message || "Failed to update customer shipment sync schedule"
        );
      }
      throw new Error("Failed to update customer shipment sync schedule");
    }
  }

  async getCustomerShipmentChannelTagMappings(): Promise<{
    mappings: CustomerShipmentChannelMappingRow[];
  }> {
    try {
      const response = await this.api.get(
        "/api/config/customer-shipment-sync/channel-tags"
      );
      return response.data;
    } catch (error) {
      if (axios.isAxiosError(error)) {
        throw new Error(
          error.response?.data?.message ||
            "Failed to fetch customer shipment channel tag mappings"
        );
      }
      throw new Error("Failed to fetch customer shipment channel tag mappings");
    }
  }

  async updateCustomerShipmentChannelTagMappings(
    mappings: CustomerShipmentChannelMappingRow[]
  ): Promise<void> {
    try {
      await this.api.put("/api/config/customer-shipment-sync/channel-tags", {
        mappings,
      });
    } catch (error) {
      if (axios.isAxiosError(error)) {
        throw new Error(
          error.response?.data?.message ||
            "Failed to update customer shipment channel tag mappings"
        );
      }
      throw new Error("Failed to update customer shipment channel tag mappings");
    }
  }

  /** Delta update: only add/update/delete changed rows by id. */
  async patchCustomerShipmentChannelTagMappings(payload: {
    added?: CustomerShipmentChannelMappingRow[];
    updated?: CustomerShipmentChannelMappingRow[];
    deleted?: number[];
  }): Promise<void> {
    try {
      await this.api.patch("/api/config/customer-shipment-sync/channel-tags", payload);
    } catch (error) {
      if (axios.isAxiosError(error)) {
        throw new Error(
          error.response?.data?.message ||
            "Failed to update customer shipment channel tag mappings"
        );
      }
      throw new Error("Failed to update customer shipment channel tag mappings");
    }
  }

  // Address Account Mappings (customer.name + delivery_address.code → Account Number, Account Zip, Bill on packing note)
  async getCustomerShipmentAddressAccountMappings(): Promise<{
    mappings: CustomerShipmentAddressAccountMappingRow[];
  }> {
    try {
      const response = await this.api.get(
        "/api/config/customer-shipment-sync/address-account-mappings"
      );
      return response.data;
    } catch (error) {
      if (axios.isAxiosError(error)) {
        throw new Error(
          error.response?.data?.message ||
            "Failed to fetch customer shipment address account mappings"
        );
      }
      throw new Error("Failed to fetch customer shipment address account mappings");
    }
  }

  async updateCustomerShipmentAddressAccountMappings(
    mappings: CustomerShipmentAddressAccountMappingRow[]
  ): Promise<void> {
    try {
      await this.api.put("/api/config/customer-shipment-sync/address-account-mappings", {
        mappings,
      });
    } catch (error) {
      if (axios.isAxiosError(error)) {
        throw new Error(
          error.response?.data?.message ||
            "Failed to update customer shipment address account mappings"
        );
      }
      throw new Error("Failed to update customer shipment address account mappings");
    }
  }

  /** Delta update: only add/update/delete changed rows by id. */
  async patchCustomerShipmentAddressAccountMappings(payload: {
    added?: CustomerShipmentAddressAccountMappingRow[];
    updated?: CustomerShipmentAddressAccountMappingRow[];
    deleted?: number[];
  }): Promise<void> {
    try {
      await this.api.patch("/api/config/customer-shipment-sync/address-account-mappings", payload);
    } catch (error) {
      if (axios.isAxiosError(error)) {
        throw new Error(
          error.response?.data?.message ||
            "Failed to update customer shipment address account mappings"
        );
      }
      throw new Error("Failed to update customer shipment address account mappings");
    }
  }

  // Service Code Mappings (Fulfil service_code → ShipHero carrier + shipping_method)
  async getServiceCodeMappings(): Promise<{
    mappings: ServiceCodeMappingItem[];
  }> {
    try {
      const response = await this.api.get("/api/config/service-code-mappings");
      return response.data;
    } catch (error) {
      if (axios.isAxiosError(error)) {
        throw new Error(
          error.response?.data?.message ||
            "Failed to fetch service code mappings"
        );
      }
      throw new Error("Failed to fetch service code mappings");
    }
  }

  async updateServiceCodeMappings(
    mappings: ServiceCodeMappingItem[]
  ): Promise<void> {
    try {
      await this.api.put("/api/config/service-code-mappings", { mappings });
    } catch (error) {
      if (axios.isAxiosError(error)) {
        throw new Error(
          error.response?.data?.message ||
            "Failed to update service code mappings"
        );
      }
      throw new Error("Failed to update service code mappings");
    }
  }

  /** Delta update: only add/update/delete changed rows by id. */
  async patchServiceCodeMappings(payload: {
    added?: ServiceCodeMappingItem[];
    updated?: ServiceCodeMappingItem[];
    deleted?: number[];
  }): Promise<void> {
    try {
      await this.api.patch("/api/config/service-code-mappings", payload);
    } catch (error) {
      if (axios.isAxiosError(error)) {
        throw new Error(
          error.response?.data?.message ||
            "Failed to update service code mappings"
        );
      }
      throw new Error("Failed to update service code mappings");
    }
  }

  async getCustomerShipmentSyncStatus(): Promise<any> {
    try {
      const response = await this.api.get("/api/customer-shipment-sync/status");
      return response.data;
    } catch (error) {
      if (axios.isAxiosError(error)) {
        throw new Error(
          error.response?.data?.message || "Failed to fetch customer shipment sync status"
        );
      }
      throw new Error("Failed to fetch customer shipment sync status");
    }
  }

  async runCustomerShipmentSync(): Promise<{ message: string; summary?: any }> {
    try {
      const response = await this.api.post("/api/customer-shipment-sync/run");
      return response.data;
    } catch (error) {
      if (axios.isAxiosError(error)) {
        if (error.response?.status === 401) {
          throw error;
        }
        throw new Error(
          error.response?.data?.message || "Failed to run customer shipment sync"
        );
      }
      throw new Error("Failed to run customer shipment sync");
    }
  }

  async listCustomerShipments(
    page = 1,
    perPage = 20,
    q?: string,
    mode?: "live" | "test",
    shipmentType?: string
  ): Promise<{
    items: any[];
    page: number;
    per_page: number;
    total: number;
    total_pages: number;
    current_mode: string;
  }> {
    try {
      const params = new URLSearchParams({
        page: page.toString(),
        per_page: perPage.toString(),
      });
      if (q) params.append("q", q);
      if (mode) params.append("mode", mode);
      if (shipmentType) params.append("shipment_type", shipmentType);
      const response = await this.api.get(`/api/customer-shipments?${params}`);
      return response.data;
    } catch (error) {
      if (axios.isAxiosError(error)) {
        throw new Error(
          error.response?.data?.message || "Failed to fetch customer shipments"
        );
      }
      throw new Error("Failed to fetch customer shipments");
    }
  }

  async retryCustomerShipmentSync(
    shipmentId: number
  ): Promise<{ status: string; message?: string; details?: any }> {
    try {
      const response = await this.api.post(
        `/api/customer-shipments/${shipmentId}/retry`
      );
      return response.data;
    } catch (error: any) {
      throw new Error(
        error.response?.data?.message || "Failed to retry customer shipment sync"
      );
    }
  }

  async syncCustomerShipmentStatus(
    orderNumber: string
  ): Promise<{ status: string; message?: string; details?: any }> {
    try {
      const response = await this.api.post(
        `/api/customer-shipments/${encodeURIComponent(orderNumber)}/sync-now`
      );
      return response.data;
    } catch (error: any) {
      throw new Error(
        error.response?.data?.message || "Failed to sync customer shipment status"
      );
    }
  }

  async getCustomerShipmentHistory(
    orderNumber: string,
    mode?: "live" | "test"
  ): Promise<{ items: any[]; total: number; current_mode: string }> {
    try {
      const params = new URLSearchParams();
      if (mode) params.append("mode", mode);
      const response = await this.api.get(
        `/api/customer-shipments/${encodeURIComponent(orderNumber)}/history?${params}`
      );
      return response.data;
    } catch (error) {
      if (axios.isAxiosError(error)) {
        throw new Error(
          error.response?.data?.message || "Failed to fetch customer shipment history"
        );
      }
      throw new Error("Failed to fetch customer shipment history");
    }
  }

  async getCustomerShipmentSyncLogs(
    page = 1,
    perPage = 20,
    q?: string,
    mode?: "live" | "test"
  ): Promise<{
    items: any[];
    page: number;
    per_page: number;
    total: number;
    total_pages: number;
  }> {
    try {
      const params = new URLSearchParams({
        page: page.toString(),
        per_page: perPage.toString(),
      });
      if (q) params.append("q", q);
      if (mode) params.append("mode", mode);

      const response = await this.api.get(`/api/customer-shipment-sync/logs?${params}`);
      return response.data;
    } catch (error) {
      if (axios.isAxiosError(error)) {
        throw new Error(
          error.response?.data?.message || "Failed to fetch customer shipment sync logs"
        );
      }
      throw new Error("Failed to fetch customer shipment sync logs");
    }
  }

  async clearCustomerShipmentSyncLogs(): Promise<{
    message: string;
    deleted_count: number;
    mode: string;
  }> {
    try {
      const response = await this.api.delete("/api/customer-shipment-sync/clear-logs");
      return response.data;
    } catch (error) {
      if (axios.isAxiosError(error)) {
        throw new Error(
          error.response?.data?.message || "Failed to clear customer shipment sync logs"
        );
      }
      throw new Error("Failed to clear customer shipment sync logs");
    }
  }

  // EDI Shipment Sync Methods
  async getEdiShipmentSyncMode(): Promise<{ mode: "live" | "test" }> {
    try {
      const response = await this.api.get("/api/config/edi-shipment-sync/mode");
      return response.data;
    } catch (error) {
      if (axios.isAxiosError(error)) {
        throw new Error(
          error.response?.data?.message || "Failed to fetch EDI shipment sync mode"
        );
      }
      throw new Error("Failed to fetch EDI shipment sync mode");
    }
  }

  async updateEdiShipmentSyncMode(mode: "live" | "test"): Promise<void> {
    try {
      await this.api.put("/api/config/edi-shipment-sync/mode", { mode });
    } catch (error) {
      if (axios.isAxiosError(error)) {
        throw new Error(
          error.response?.data?.message || "Failed to update EDI shipment sync mode"
        );
      }
      throw new Error("Failed to update EDI shipment sync mode");
    }
  }

  async getEdiShipmentSyncSchedule(): Promise<{ schedule: string[] }> {
    try {
      const response = await this.api.get("/api/config/edi-shipment-sync/schedule");
      return response.data;
    } catch (error) {
      if (axios.isAxiosError(error)) {
        throw new Error(
          error.response?.data?.message || "Failed to fetch EDI shipment sync schedule"
        );
      }
      throw new Error("Failed to fetch EDI shipment sync schedule");
    }
  }

  async updateEdiShipmentSyncSchedule(schedule: string[]): Promise<void> {
    try {
      await this.api.put("/api/config/edi-shipment-sync/schedule", { schedule });
    } catch (error) {
      if (axios.isAxiosError(error)) {
        throw new Error(
          error.response?.data?.message || "Failed to update EDI shipment sync schedule"
        );
      }
      throw new Error("Failed to update EDI shipment sync schedule");
    }
  }

  async getEdiShipmentChannelTagMappings(): Promise<{
    mappings: EdiShipmentChannelMappingRow[];
  }> {
    try {
      const response = await this.api.get(
        "/api/config/edi-shipment-sync/channel-tags"
      );
      return response.data;
    } catch (error) {
      if (axios.isAxiosError(error)) {
        throw new Error(
          error.response?.data?.message ||
            "Failed to fetch EDI shipment channel tag mappings"
        );
      }
      throw new Error("Failed to fetch EDI shipment channel tag mappings");
    }
  }

  async updateEdiShipmentChannelTagMappings(
    mappings: EdiShipmentChannelMappingRow[]
  ): Promise<void> {
    try {
      await this.api.put("/api/config/edi-shipment-sync/channel-tags", {
        mappings,
      });
    } catch (error) {
      if (axios.isAxiosError(error)) {
        throw new Error(
          error.response?.data?.message ||
            "Failed to update EDI shipment channel tag mappings"
        );
      }
      throw new Error("Failed to update EDI shipment channel tag mappings");
    }
  }

  /** Delta update: only add/update/delete changed rows by id. */
  async patchEdiShipmentChannelTagMappings(payload: {
    added?: EdiShipmentChannelMappingRow[];
    updated?: EdiShipmentChannelMappingRow[];
    deleted?: number[];
  }): Promise<void> {
    try {
      await this.api.patch("/api/config/edi-shipment-sync/channel-tags", payload);
    } catch (error) {
      if (axios.isAxiosError(error)) {
        throw new Error(
          error.response?.data?.message ||
            "Failed to update EDI shipment channel tag mappings"
        );
      }
      throw new Error("Failed to update EDI shipment channel tag mappings");
    }
  }

  async getEdiShipmentChannelEmailMappings(): Promise<{
    mappings: { id: number; channel: string; emails: string[] }[];
  }> {
    try {
      const response = await this.api.get(
        "/api/config/edi-shipment-sync/channel-emails"
      );
      return response.data;
    } catch (error) {
      if (axios.isAxiosError(error)) {
        throw new Error(
          error.response?.data?.message ||
            "Failed to fetch EDI shipment channel email mappings"
        );
      }
      throw new Error("Failed to fetch EDI shipment channel email mappings");
    }
  }

  async updateEdiShipmentChannelEmailMappings(
    mappings: { id?: number; emails: string[] }[]
  ): Promise<void> {
    try {
      await this.api.put("/api/config/edi-shipment-sync/channel-emails", {
        mappings,
      });
    } catch (error) {
      if (axios.isAxiosError(error)) {
        throw new Error(
          error.response?.data?.message ||
            "Failed to update EDI shipment channel email mappings"
        );
      }
      throw new Error("Failed to update EDI shipment channel email mappings");
    }
  }

  // Wholesale Routing
  async getWholesaleRouting(
    page = 1,
    perPage = 20,
    status = "pending",
    q = "",
    mode?: "live" | "test"
  ): Promise<{
    items: any[];
    page: number;
    per_page: number;
    total: number;
    total_pages: number;
  }> {
    try {
      const params = new URLSearchParams();
      params.set("page", String(page));
      params.set("per_page", String(perPage));
      if (status) {
        params.set("status", status);
      }
      if (q) {
        params.set("q", q);
      }
      if (mode) {
        params.set("mode", mode);
      }

      const response = await this.api.get(`/api/wholesale-routing?${params.toString()}`);
      return response.data;
    } catch (error) {
      if (axios.isAxiosError(error)) {
        throw new Error(
          error.response?.data?.message || "Failed to fetch wholesale routing"
        );
      }
      throw new Error("Failed to fetch wholesale routing");
    }
  }

  async updateWholesaleRouting(id: number, data: any): Promise<void> {
    try {
      await this.api.put(`/api/wholesale-routing/${id}`, data);
    } catch (error) {
      if (axios.isAxiosError(error)) {
        throw new Error(
          error.response?.data?.message || "Failed to update wholesale routing"
        );
      }
      throw new Error("Failed to update wholesale routing");
    }
  }

  async getEdiShipmentSyncStatus(): Promise<any> {
    try {
      const response = await this.api.get("/api/edi-shipment-sync/status");
      return response.data;
    } catch (error) {
      if (axios.isAxiosError(error)) {
        throw new Error(
          error.response?.data?.message || "Failed to fetch EDI shipment sync status"
        );
      }
      throw new Error("Failed to fetch EDI shipment sync status");
    }
  }

  async runEdiShipmentSync(): Promise<{ message: string; summary?: any; warnings?: string[]; is_syncing?: boolean }> {
    try {
      const response = await this.api.post("/api/edi-shipment-sync/run");
      return response.data;
    } catch (error) {
      if (axios.isAxiosError(error)) {
        if (error.response?.status === 401) {
          throw error;
        }
        throw new Error(
          error.response?.data?.message || "Failed to run EDI shipment sync"
        );
      }
      throw new Error("Failed to run EDI shipment sync");
    }
  }

  async listEdiShipments(
    page = 1,
    perPage = 20,
    q?: string,
    mode?: "live" | "test"
  ): Promise<{
    items: any[];
    page: number;
    per_page: number;
    total: number;
    total_pages: number;
    current_mode: string;
  }> {
    try {
      const params = new URLSearchParams({
        page: page.toString(),
        per_page: perPage.toString(),
      });
      if (q) params.append("q", q);
      if (mode) params.append("mode", mode);
      const response = await this.api.get(`/api/edi-shipments?${params}`);
      return response.data;
    } catch (error) {
      if (axios.isAxiosError(error)) {
        throw new Error(
          error.response?.data?.message || "Failed to fetch EDI shipments"
        );
      }
      throw new Error("Failed to fetch EDI shipments");
    }
  }

  async retryEdiShipmentSync(
    shipmentId: number
  ): Promise<{ status: string; message?: string; details?: any }> {
    try {
      const response = await this.api.post(
        `/api/edi-shipments/${shipmentId}/retry`
      );
      return response.data;
    } catch (error: any) {
      throw new Error(
        error.response?.data?.message || "Failed to retry EDI shipment sync"
      );
    }
  }

  async getEdiShipmentHistory(
    orderNumber: string,
    mode?: "live" | "test"
  ): Promise<{ items: any[]; total: number; current_mode: string }> {
    try {
      const params = new URLSearchParams();
      if (mode) params.append("mode", mode);
      const response = await this.api.get(
        `/api/edi-shipments/${encodeURIComponent(orderNumber)}/history?${params}`
      );
      return response.data;
    } catch (error) {
      if (axios.isAxiosError(error)) {
        throw new Error(
          error.response?.data?.message || "Failed to fetch EDI shipment history"
        );
      }
      throw new Error("Failed to fetch EDI shipment history");
    }
  }

  async getEdiShipmentSyncLogs(
    page = 1,
    perPage = 20,
    q?: string,
    mode?: "live" | "test"
  ): Promise<{
    items: any[];
    page: number;
    per_page: number;
    total: number;
    total_pages: number;
  }> {
    try {
      const params = new URLSearchParams({
        page: page.toString(),
        per_page: perPage.toString(),
      });
      if (q) params.append("q", q);
      if (mode) params.append("mode", mode);

      const response = await this.api.get(`/api/edi-shipment-sync/logs?${params}`);
      return response.data;
    } catch (error) {
      if (axios.isAxiosError(error)) {
        throw new Error(
          error.response?.data?.message || "Failed to fetch EDI shipment sync logs"
        );
      }
      throw new Error("Failed to fetch EDI shipment sync logs");
    }
  }

  async clearEdiShipmentSyncLogs(): Promise<{
    message: string;
    deleted_count: number;
    mode: string;
  }> {
    try {
      const response = await this.api.delete("/api/edi-shipment-sync/clear-logs");
      return response.data;
    } catch (error) {
      if (axios.isAxiosError(error)) {
        throw new Error(
          error.response?.data?.message || "Failed to clear EDI shipment sync logs"
        );
      }
      throw new Error("Failed to clear EDI shipment sync logs");
    }
  }
}

export const configService = new ConfigService();
